/*
  Communite predictive search
  Cancels stale requests, keeps combobox focus on the input and supports editor reloads.
*/

class CommunitePredictiveSearch extends HTMLElement {
  connectedCallback () {
    if (this.initialized) return

    this.initialized = true
    this.form = this.querySelector('[data-cm-predictive-form]')
    this.input = this.querySelector('[data-cm-predictive-input]')
    this.panel = this.querySelector('[data-cm-predictive-panel]')
    this.results = this.querySelector('[data-cm-predictive-results]')
    this.intro = this.querySelector('[data-cm-predictive-intro]')
    this.loading = this.querySelector('[data-cm-predictive-loading]')
    this.error = this.querySelector('[data-cm-predictive-error]')
    this.status = this.querySelector('[data-cm-predictive-status]')
    this.abortController = new AbortController()
    this.signal = this.abortController.signal
    this.fetchController = null
    this.debounceTimer = null
    this.requestSequence = 0
    this.activeIndex = -1
    this.lastQuery = ''
    this.suppressFocusOpen = false

    if (!this.form || !this.input || !this.panel || !this.results) return

    this.input.addEventListener('input', () => this.handleInput(), { signal: this.signal })
    this.input.addEventListener('focus', () => this.handleFocus(), { signal: this.signal })
    this.input.addEventListener('keydown', event => this.handleKeydown(event), { signal: this.signal })
    this.form.addEventListener('submit', () => this.close(false), { signal: this.signal })
    this.addEventListener('click', event => this.handleClick(event), { signal: this.signal })
    document.addEventListener('pointerdown', event => this.handleDocumentPointerDown(event), { signal: this.signal })
  }

  disconnectedCallback () {
    window.clearTimeout(this.debounceTimer)
    this.fetchController?.abort()
    this.abortController?.abort()
    this.initialized = false
  }

  handleInput () {
    const query = this.input.value.trim()

    window.clearTimeout(this.debounceTimer)
    this.clearSelection()

    if (!query) {
      this.fetchController?.abort()
      this.lastQuery = ''
      this.results.replaceChildren()
      this.setLoading(false)
      this.hideError()
      this.showIntro(true)
      this.open()
      this.announce(this.dataset.emptyMessage)
      return
    }

    this.showIntro(false)
    this.hideError()
    this.open()
    this.debounceTimer = window.setTimeout(() => this.search(query), 250)
  }

  handleFocus () {
    if (this.suppressFocusOpen) return

    const query = this.input.value.trim()

    if (!query) {
      this.showIntro(true)
      this.open()
      return
    }

    if (query !== this.lastQuery || !this.results.childElementCount) {
      this.search(query)
    } else {
      this.open()
    }
  }

  handleClick (event) {
    const closeButton = event.target.closest('[data-cm-predictive-close]')

    if (closeButton && this.contains(closeButton)) {
      event.preventDefault()
      this.close(true)
      return
    }

    const retryButton = event.target.closest('[data-cm-predictive-retry]')

    if (retryButton && this.contains(retryButton)) {
      event.preventDefault()
      const query = this.input.value.trim()
      if (query) this.search(query)
    }
  }

  handleDocumentPointerDown (event) {
    if (!this.opened || this.contains(event.target)) return
    this.close(false)
  }

  handleKeydown (event) {
    if (event.key === 'Escape') {
      if (!this.opened) return
      event.preventDefault()
      this.close(true)
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return

    const options = this.getOptions()
    if (!options.length) return

    if (event.key === 'Enter') {
      if (this.activeIndex < 0) return

      const activeLink = options[this.activeIndex].matches('a')
        ? options[this.activeIndex]
        : options[this.activeIndex].querySelector('a')

      if (activeLink) {
        event.preventDefault()
        activeLink.click()
      }
      return
    }

    event.preventDefault()
    this.open()

    if (event.key === 'ArrowDown') {
      this.activeIndex = this.activeIndex >= options.length - 1 ? 0 : this.activeIndex + 1
    } else {
      this.activeIndex = this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1
    }

    this.selectOption(options)
  }

  getOptions () {
    return [...this.results.querySelectorAll('[data-cm-predictive-option]')]
      .filter(option => !option.hidden)
  }

  selectOption (options) {
    options.forEach((option, index) => {
      const selected = index === this.activeIndex
      option.setAttribute('aria-selected', String(selected))

      if (!selected) return

      if (!option.id) {
        option.id = `${this.input.id}-option-${index + 1}`
      }

      this.input.setAttribute('aria-activedescendant', option.id)
      option.scrollIntoView({ block: 'nearest' })
      this.announce(option.textContent.trim().replace(/\s+/g, ' '))
    })
  }

  clearSelection () {
    this.activeIndex = -1
    this.input.removeAttribute('aria-activedescendant')
    this.getOptions().forEach(option => option.setAttribute('aria-selected', 'false'))
  }

  async search (query) {
    const normalizedQuery = query.trim()
    if (!normalizedQuery) return

    const requestId = ++this.requestSequence
    const requestUrl = new URL(window.Shopify.routes.predictive_search_url, window.location.origin)
    requestUrl.searchParams.set('q', normalizedQuery)
    requestUrl.searchParams.set('resources[type]', this.dataset.resourcesType)
    requestUrl.searchParams.set('resources[limit]', '8')
    requestUrl.searchParams.set('resources[limit_scope]', 'each')
    requestUrl.searchParams.set('section_id', 'predictive-search')

    this.fetchController?.abort()
    this.fetchController = new AbortController()
    this.lastQuery = normalizedQuery
    this.showIntro(false)
    this.hideError()
    this.setLoading(true)
    this.open()

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: this.fetchController.signal
      })

      if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`)

      const html = await response.text()
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html')
      const markup = parsedDocument.querySelector('[data-cm-predictive-results-markup]')

      if (!markup) throw new Error('Predictive search results missing from response')
      if (requestId !== this.requestSequence || this.input.value.trim() !== normalizedQuery) return

      this.results.replaceChildren(markup)
      this.clearSelection()
      this.hideError()
      this.setLoading(false)
      this.announce(markup.dataset.resultsText)
    } catch (error) {
      if (error.name === 'AbortError') return
      if (requestId !== this.requestSequence) return

      this.setLoading(false)
      this.showError()
    }
  }

  setLoading (isLoading) {
    this.toggleAttribute('aria-busy', isLoading)
    if (this.loading) this.loading.hidden = !isLoading
    if (isLoading) this.results.replaceChildren()
    if (isLoading) this.announce(this.dataset.loadingMessage)
  }

  showIntro (show) {
    if (this.intro) this.intro.hidden = !show
  }

  hideError () {
    if (this.error) this.error.hidden = true
  }

  showError () {
    const message = this.dataset.errorMessage
    const messageElement = this.error?.querySelector('[data-cm-predictive-error-message]')

    if (messageElement && message) messageElement.textContent = message
    if (this.error) this.error.hidden = false
    this.announce(message)
  }

  announce (message) {
    if (!this.status || !message) return

    this.status.textContent = ''
    window.requestAnimationFrame(() => {
      this.status.textContent = message
    })
  }

  open () {
    if (this.opened) return

    document.dispatchEvent(new CustomEvent('cm:predictive-search:before-open', {
      detail: { search: this }
    }))

    this.opened = true
    this.classList.add('is-open')
    this.panel.hidden = false
    this.input.setAttribute('aria-expanded', 'true')
    document.documentElement.classList.toggle(
      'cm-predictive-search-is-open',
      window.matchMedia('(max-width: 63.999rem)').matches
    )
  }

  close (restoreFocus) {
    if (!this.opened) return

    this.opened = false
    this.classList.remove('is-open')
    this.panel.hidden = true
    this.input.setAttribute('aria-expanded', 'false')
    this.clearSelection()
    document.documentElement.classList.remove('cm-predictive-search-is-open')

    if (restoreFocus && this.input.isConnected) {
      this.suppressFocusOpen = true
      window.requestAnimationFrame(() => {
        this.input.focus({ preventScroll: true })
        window.setTimeout(() => {
          this.suppressFocusOpen = false
        }, 0)
      })
    }
  }
}

if (!customElements.get('cm-predictive-search')) {
  customElements.define('cm-predictive-search', CommunitePredictiveSearch)
}

document.addEventListener('cm:predictive-search:before-open', event => {
  document.querySelectorAll('cm-predictive-search.is-open').forEach(search => {
    if (search !== event.detail?.search) search.close(false)
  })
})
